import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { GeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';
import { getMapConfig, MapVariableConfig } from '../../config/mapConfigs';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { getCountyFromGeoid, normalizeCensusTractGeoid } from '../../utils/countyFipsMapping';
import {
  CHOROPLETH_BASEMAP_OPACITY,
  choroplethDataPathStyle,
  choroplethHoverPathStyle,
  choroplethNoDataPathStyle,
} from '../../utils/choroplethDesignSystem';
import { MAP_REFERENCE_DEFINITIONS } from '../../utils/mapReferenceStatistics';
import { fetchPlaceTractSet } from '../../services/walkabilityTransportMetrics';
import {
  locationKpiLabel,
  rowsForSelectedLocation,
  STATEWIDE_FETCH_SELECT,
  summarizeJurisdiction,
  summarizeMapScopePeer,
  tractValuesFromRows,
  type MapScopeLabelContext,
} from '../../utils/mapTractStatistics';
import { zeroVehicleHouseholdPctFromRow } from '../../utils/genericMapClassification';
import { isMapReadyForLayout, safeInvalidateSize } from '../../utils/leafletBounds';
import {
  buildClassificationForVariable,
  extractNumericValuesForVariable,
  MAP_VARIABLE_SEMANTICS,
  tractTotalEvsFromRow,
  type MapVariableId,
} from '../../utils/genericMapClassification';
import { resolveCountyPrefixForLocation } from '../../services/walkabilityTransportMetrics';
import MapLegend from './MapLegend';
import MapVariableSelector from './MapVariableSelector';
import DataLoadingScopeSelector from './DataLoadingScopeSelector';
import { downloadDomAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

interface GenericMapProps {
  location: string;
  /** Place/city geofips — used to limit KPI tracts to the selected city. */
  locationGeofips?: string | number | null;
  onDataLoaded?: (metrics: MapMetrics) => void;
  hideVariableSelector?: boolean;
  lockedVariable?: string; // Lock to a specific variable (e.g., 'ev_adoption', 'total_chargers')
  allowedVariables?: string[]; // Optional subset of map variables to expose in the selector
  enableScopeSelector?: boolean; // Show data scope (county/region/California) selector
  onDownloadPNGReady?: (handler: () => void) => void;
  onDownloadCSVReady?: (handler: () => void) => void;
  /** Map panel height in pixels (default 500). */
  mapHeightPx?: number;
}

interface TractData {
  geofips: string;
  county: string;
  location_name: string;
  [key: string]: any; // Allow for different data fields
}

export type MapMetrics = {
  currentValue: number;
  californiaAverage: number | null;
  year: number;
  variable: string;
  /** KPI label for loaded geography statistic */
  jurisdictionLabel: string;
  /** KPI label for California / reference statistic */
  referenceLabel: string;
  minValue?: number;
  maxValue?: number;
  tractCount?: number;
  medianTractValue?: number;
  zeroPortTractCount?: number;
  evCountEstimated?: boolean;
};

type CachedMapData = {
  tractData: TractData[];
  mapCenter: [number, number];
  mapZoom: number;
  metrics: MapMetrics | null;
};

const MAP_CACHE_MAX = 5;
const mapDataCache = new Map<string, CachedMapData>();

const statewideValuesCache = new Map<string, number[]>();
/** Tract rows shared across map variables with the same Supabase source + geography. */
const tractRowCache = new Map<string, any[]>();

async function fetchStatewideNumericValues(
  config: MapVariableConfig,
  getField: (id: MapVariableId) => string
): Promise<number[] | null> {
  const varId = config.id as MapVariableId;
  if (!MAP_VARIABLE_SEMANTICS[varId]) return null;
  const cacheKey = `${config.dataSource}:${varId}:v3`;
  if (statewideValuesCache.has(cacheKey)) {
    return statewideValuesCache.get(cacheKey)!;
  }

  const pageSize = 5000;
  let offset = 0;
  const rows: any[] = [];

  try {
    if (config.dataSource === 'ev_by_tract_2023') {
      // Full row select — partial column lists 400 when a name is wrong (e.g. geofips vs tract).
      while (true) {
        const { data, error } = await supabaseClient
          .from('ev_by_tract_2023')
          .select('*')
          .range(offset, offset + pageSize - 1);
        if (error) throw error;
        if (!data?.length) break;
        rows.push(...data);
        if (data.length < pageSize) break;
        offset += pageSize;
      }
    } else if (config.dataSource === 'cbei_tracts') {
      while (true) {
        const { data, error } = await supabaseClient
          .from('cbei_tracts')
          .select(STATEWIDE_FETCH_SELECT.cbei_tracts)
          .eq('year', 2023)
          .range(offset, offset + pageSize - 1);
        if (error) throw error;
        if (!data?.length) break;
        rows.push(...data);
        if (data.length < pageSize) break;
        offset += pageSize;
      }
    } else if (config.dataSource === 'acs5_income') {
      const incomeField = getField(varId);
      const { data: yearRow } = await supabaseClient
        .from('acs5_income')
        .select('year')
        .order('year', { ascending: false })
        .limit(1);
      const latestYear = yearRow?.[0]?.year ?? 2023;
      while (true) {
        const { data, error } = await supabaseClient
          .from('acs5_income')
          .select(incomeField)
          .eq('year', latestYear)
          .range(offset, offset + pageSize - 1);
        if (error) throw error;
        if (!data?.length) break;
        rows.push(...data);
        if (data.length < pageSize) break;
        offset += pageSize;
      }
    } else {
      return null;
    }

    const values = extractNumericValuesForVariable(rows, varId, getField);
    statewideValuesCache.set(cacheKey, values);
    return values;
  } catch (e) {
    console.warn('Statewide break fetch failed', config.id, e);
    return null;
  }
}

function mapCacheSet(key: string, value: CachedMapData) {
  if (mapDataCache.size >= MAP_CACHE_MAX) {
    const oldest = mapDataCache.keys().next().value;
    if (oldest !== undefined) mapDataCache.delete(oldest);
  }
  mapDataCache.set(key, value);
}

const GenericMapTiered: React.FC<GenericMapProps> = ({
  location,
  locationGeofips = null,
  onDataLoaded,
  hideVariableSelector = false,
  lockedVariable,
  allowedVariables,
  enableScopeSelector = false,
  onDownloadPNGReady,
  onDownloadCSVReady,
  mapHeightPx = 500,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariable, setSelectedVariable] = useState<string>(() => {
    // Prefer a locked variable if provided
    if (lockedVariable) return lockedVariable;
    // Otherwise, if a constrained set of variables is provided, default to the first one
    if (allowedVariables && allowedVariables.length > 0) {
      return allowedVariables[0];
    }
    // Fallback to carbon_footprint for legacy usages
    return 'carbon_footprint';
  });
  const [tractData, setTractData] = useState<TractData[]>([]);
  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [loadingStage, setLoadingStage] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [mapCenter, setMapCenter] = useState<[number, number]>([36.7783, -119.4179]); // Default California center
  const [mapZoom, setMapZoom] = useState<number>(6); // Default California zoom
  const [mapReady, setMapReady] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false); // Prevent multiple simultaneous loads
  const loadGenerationRef = useRef(0); // Ignore stale async completions after variable/scope changes
  const lastGeographyKeyRef = useRef<string>(''); // location + scope — reset view only when this changes
  const liveMapViewRef = useRef<{ center: [number, number]; zoom: number }>({
    center: [36.7783, -119.4179],
    zoom: 6,
  });
  const isMountedRef = useRef(true);
  const [dataScope, setDataScope] = useState<'county' | 'region' | 'california'>('county');
  /** When set, only render tracts in this 5-digit state+county FIPS prefix (e.g. 06037). */
  const [countyGeoidPrefix, setCountyGeoidPrefix] = useState<string | null>(null);
  const [statewideBreakValues, setStatewideBreakValues] = useState<number[] | null>(null);
  const [usesStatewideBreaks, setUsesStatewideBreaks] = useState(false);
  const [placeTractGeoids, setPlaceTractGeoids] = useState<Set<string> | null>(null);
  const [mapScopeLabels, setMapScopeLabels] = useState<MapScopeLabelContext>({});

  const onDataLoadedRef = useRef(onDataLoaded);
  onDataLoadedRef.current = onDataLoaded;
  const lastMetricsNotifyKeyRef = useRef<string | null>(null);

  const metricsNotifyKey = (metrics: MapMetrics) =>
    `${metrics.variable}|${metrics.year}|${metrics.currentValue}|${metrics.californiaAverage}|${metrics.jurisdictionLabel}`;

  const notifyDataLoaded = useCallback((metrics: MapMetrics) => {
    const key = metricsNotifyKey(metrics);
    if (lastMetricsNotifyKeyRef.current === key) return;
    lastMetricsNotifyKeyRef.current = key;
    onDataLoadedRef.current?.(metrics);
  }, []);

  useEffect(() => {
    lastMetricsNotifyKeyRef.current = null;
  }, [location, selectedVariable, dataScope]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Update selectedVariable when lockedVariable changes
  useEffect(() => {
    if (lockedVariable) {
      setSelectedVariable(lockedVariable);
    }
  }, [lockedVariable]);

  // When a set of allowed variables is provided (e.g., on People/Transportation pages),
  // make sure the selected variable is one of them, defaulting to the first allowed option.
  useEffect(() => {
    if (!lockedVariable && allowedVariables && allowedVariables.length > 0) {
      setSelectedVariable((prev) =>
        allowedVariables.includes(prev) ? prev : allowedVariables[0]
      );
    }
  }, [allowedVariables, lockedVariable]);

  // Get current map configuration
  const currentConfig = useMemo(() => getMapConfig(selectedVariable), [selectedVariable]);

  // Function to get map center based on county
  const getMapCenter = (county: string): [number, number] => {
    const countyCenters: Record<string, [number, number]> = {
      'Alameda County': [37.7749, -122.2711], // Oakland area
      'Alpine County': [38.5974, -119.8193], // Alpine
      'Amador County': [38.4467, -120.6511], // Jackson
      'Butte County': [39.6679, -121.6008], // Chico
      'Calaveras County': [38.2041, -120.5547], // San Andreas
      'Colusa County': [39.2143, -122.0094], // Colusa
      'Contra Costa County': [37.9199, -122.3482], // Martinez
      'Del Norte County': [41.7558, -124.2016], // Crescent City
      'El Dorado County': [38.7786, -120.5247], // Placerville
      'Fresno County': [36.7378, -119.7871], // Fresno
      'Glenn County': [39.5985, -122.3920], // Willows
      'Humboldt County': [40.8665, -124.0828], // Eureka
      'Imperial County': [32.8475, -115.5694], // El Centro
      'Inyo County': [36.5110, -117.3104], // Independence
      'Kern County': [35.3433, -118.7272], // Bakersfield
      'Kings County': [36.0753, -119.8157], // Hanford
      'Lake County': [39.0586, -122.9155], // Lakeport
      'Lassen County': [40.4174, -120.6506], // Susanville
      'Los Angeles County': [34.0522, -118.2437], // LA
      'Madera County': [37.2181, -119.7619], // Madera
      'Marin County': [38.0834, -122.7633], // San Rafael
      'Mariposa County': [37.4849, -119.9663], // Mariposa
      'Mendocino County': [39.3077, -123.7995], // Ukiah
      'Merced County': [37.3022, -120.4829], // Merced
      'Modoc County': [41.5898, -120.7250], // Alturas
      'Mono County': [37.9389, -118.8870], // Bridgeport
      'Monterey County': [36.6002, -121.8947], // Salinas
      'Napa County': [38.2975, -122.2869], // Napa
      'Nevada County': [39.2619, -121.0161], // Nevada City
      'Orange County': [33.7175, -117.8311], // Orange County
      'Placer County': [39.0000, -120.7500], // Auburn
      'Plumas County': [40.0037, -120.8397], // Quincy
      'Riverside County': [33.9533, -117.3962], // Riverside
      'Sacramento County': [38.5816, -121.4944], // Sacramento
      'San Benito County': [36.6061, -121.0756], // Hollister
      'San Bernardino County': [34.1083, -117.2898], // San Bernardino
      'San Diego County': [32.7157, -117.1611], // San Diego
      'San Francisco County': [37.7749, -122.4194], // SF
      'San Joaquin County': [37.7799, -121.9780], // Stockton
      'San Luis Obispo County': [35.2828, -120.6596], // San Luis Obispo
      'San Mateo County': [37.4969, -122.3331], // Redwood City
      'Santa Barbara County': [34.4221, -119.7027], // Santa Barbara
      'Santa Clara County': [37.3541, -121.9552], // San Jose
      'Santa Cruz County': [37.0351, -122.0096], // Santa Cruz
      'Shasta County': [40.5865, -122.3917], // Redding
      'Sierra County': [39.5800, -120.5169], // Downieville
      'Siskiyou County': [41.5889, -122.5406], // Yreka
      'Solano County': [38.2784, -122.0093], // Fairfield
      'Sonoma County': [38.5270, -122.9295], // Santa Rosa
      'Stanislaus County': [37.5591, -120.9969], // Modesto
      'Sutter County': [39.0347, -121.7536], // Yuba City
      'Tehama County': [40.1255, -122.2347], // Red Bluff
      'Trinity County': [40.6507, -123.1126], // Weaverville
      'Tulare County': [36.2077, -119.3473], // Visalia
      'Tuolumne County': [37.9411, -120.2319], // Sonora
      'Ventura County': [34.3705, -119.1391], // Ventura
      'Yolo County': [38.6845, -121.7732], // Davis area
      'Yuba County': [39.1404, -121.6169], // Marysville
    };
    
    return countyCenters[county] || [36.7783, -119.4179]; // Default to California center
  };

  // Function to get region information for a location
  const getLocationRegionInfo = async (locationName: string): Promise<{county: string, region: number, regionName: string, geofips: number} | null> => {
    try {
      const { data, error } = await supabaseClient
        .from('regions')
        .select('geofips, county, region, region_name')
        .ilike('location_name', locationName) // Use ilike for case-insensitive search
        .eq('geography_type', 'City') // Use 'City' with capital C as shown in the data
        .limit(1);
      
      if (error) {
        console.error('Error fetching region info:', error);
        throw error;
      }
      if (!data || data.length === 0) {
        return null;
      }
      
      return {
        geofips: data[0].geofips,
        county: data[0].county,
        region: data[0].region,
        regionName: data[0].region_name
      };
    } catch (err) {
      console.error('Error fetching region info:', err);
      return null;
    }
  };

  // Resolve data field for the selected variable
  const getDataFieldForVariable = useCallback((variableId?: string): string => {
    if (variableId === 'income') return 'b19013_001e';
    if (variableId === 'ev_adoption') return 'evs_per_100_people';
    if (variableId === 'total_evs') return 'evs_per_100_people';
    if (variableId === 'total_chargers') return 'total_chargers';
    if (variableId === 'zero_vehicle_households') return 'pct_zero_vehicle_households';
    if (variableId === 'vehicles_per_household') return 'vehicles_per_household';
    if (variableId === 'population_density') return 'persons_per_sqmi';
    if (variableId === 'political_lean') return 'dem_minus_rep';
    if (variableId === 'carbon_footprint') return 'total_hh';
    if (variableId === 'commute_time') return 'avg_travel_time';
    if (variableId === 'owner_occupied_housing') return 'pct_owner_occupied';
    if (variableId === 'educational_attainment') return 'pct_bachelor_or_higher';
    return 'value'; // Default field name
  }, []);

  // Load GeoJSON data
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch('/data/california_tracts_optimized.geojson');
        const data = await response.json();
        setGeoJsonData(data);
      } catch (err) {
        console.error('Error loading GeoJSON:', err);
        setError('Failed to load map data');
      }
    };

    loadGeoJSON();
  }, []);

  // Data loading function based on location and scope (county/region/California)
  const loadDataByLocation = useCallback(async () => {
    if (!currentConfig) {
      console.error('❌ No config found for variable:', selectedVariable);
      setError(`No configuration found for variable: ${selectedVariable}`);
      setLoading(false);
      setLoadingProgress(0);
      return;
    }

    const geographyKey = `${location}-${dataScope}`;
    const shouldResetMapView = lastGeographyKeyRef.current !== geographyKey;

    const loadKey = `${selectedVariable}-${location}-${dataScope}-v2`;
    const tractCacheKey = `${location}-${dataScope}-${currentConfig.dataSource}-tract-v3`;

    const cached = mapDataCache.get(loadKey);
    if (cached) {
      setLoading(false);
      setError(null);
      setLoadingStage('');
      setLoadingProgress(100);
      setTractData(cached.tractData);
      setMapReady(true);
      if (cached.metrics) {
        notifyDataLoaded(cached.metrics);
      }
      return;
    }

    const cachedTractRows = tractRowCache.get(tractCacheKey);
    if (cachedTractRows && cachedTractRows.length > 0) {
      setLoading(false);
      setError(null);
      setLoadingStage('');
      setLoadingProgress(100);
      setTractData(cachedTractRows);
      setMapReady(true);
      return;
    }

    // Prevent multiple simultaneous loads (reset when variable changes — see useEffect below).
    if (isLoadingRef.current) {
      return;
    }

    const loadGeneration = ++loadGenerationRef.current;
    const isStaleLoad = () => loadGeneration !== loadGenerationRef.current;
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);
    setLoadingProgress(0);

    try {
      // Initialize location service if needed
      await supabaseLocationService.initialize();
      
      // Get location info to determine if it's a city, county, or state
      const locations = supabaseLocationService.getAvailableLocations();
      const locationInfo = locations.find(loc => loc.name === location);
      
      let allData: any[] = [];
      let nextMapCenter: [number, number] | null = null;
      let nextMapZoom: number | null = null;
      let targetCounty: string | null = null;
      let isState = false;
      let regionInfo: { county: string; region: number; regionName: string; geofips: number } | null = null;

      // Determine base geography for the selected location
      if (!locationInfo) {
        // Fallback: try to determine from location name
        if (location.toLowerCase() === 'california' || location.toLowerCase().includes('state')) {
          isState = true;
        } else if (location.toLowerCase().endsWith(' county')) {
          targetCounty = location.replace(/ County$/i, '');
        } else {
          // Assume it's a city and try to get county using the service method
          targetCounty = supabaseLocationService.getCountyForCity(location);
        }
      } else {
        // Use location info
        if (locationInfo.type === 'state') {
          isState = true;
        } else if (locationInfo.type === 'county') {
          targetCounty = locationInfo.name.replace(/ County$/i, '');
        } else if (locationInfo.type === 'city') {
          // For cities, show the county's tracts (since cities span multiple tracts)
          // Remove " County" suffix if present to normalize
          targetCounty = locationInfo.county.replace(/ County$/i, '');
        }
      }

      // Override geography based on selected data scope
      if (dataScope === 'california') {
        isState = true;
      } else if (dataScope === 'region') {
        try {
          // Region info is currently defined for City locations in the regions table
          regionInfo = await getLocationRegionInfo(location);
        } catch (regionErr) {
          // Fall back to county scope
        }
      }
      
      if (isState) {
        setCountyGeoidPrefix(null);
        // Load all California data
        setLoadingStage('Loading California data...');
        setLoadingProgress(20);
        nextMapCenter = [36.7783, -119.4179]; // California center
        nextMapZoom = 6; // Zoom out to show all of California
        if (shouldResetMapView) {
          setMapCenter(nextMapCenter);
          setMapZoom(nextMapZoom);
          liveMapViewRef.current = { center: nextMapCenter, zoom: nextMapZoom };
          lastGeographyKeyRef.current = geographyKey;
        }

        // Load all California data (pass empty array to get all data)
        try {
          setLoadingProgress(30);
          const californiaData = await Promise.race([
            loadRemainingCaliforniaData([], currentConfig),
            new Promise<any[]>((_, reject) => 
              setTimeout(() => reject(new Error('Data loading timeout after 30 seconds')), 30000)
            )
          ]);
          
          if (isStaleLoad()) return;
          if (californiaData && californiaData.length > 0) {
            allData = [...californiaData];
            tractRowCache.set(tractCacheKey, allData);
            setTractData(allData);
            setLoadingProgress(90);
            const metrics = await calculateAndSetMetrics(allData, currentConfig);
            if (metrics && nextMapCenter && nextMapZoom) {
              mapCacheSet(loadKey, {
                tractData: allData,
                mapCenter: liveMapViewRef.current.center,
                mapZoom: liveMapViewRef.current.zoom,
                metrics
              });
            }
          } else {
            setError('No California data found');
          }
        } catch (loadError) {
          console.error('Error loading California data:', loadError);
          setError(`Failed to load California data: ${loadError instanceof Error ? loadError.message : 'Unknown error'}`);
        }
      } else if (regionInfo && dataScope === 'region') {
        setCountyGeoidPrefix(null);
        // Load data for the full region (set of counties)
        setLoadingStage(`Loading ${regionInfo.regionName || 'regional'} data...`);
        setLoadingProgress(20);

        // Center map on the primary county for this region
        const regionCountyCenter = getMapCenter(`${regionInfo.county}`);
        nextMapCenter = regionCountyCenter;
        nextMapZoom = 7; // Slightly zoomed out to show multiple counties
        if (shouldResetMapView) {
          setMapCenter(nextMapCenter);
          setMapZoom(nextMapZoom);
          liveMapViewRef.current = { center: nextMapCenter, zoom: nextMapZoom };
          lastGeographyKeyRef.current = geographyKey;
        }

        try {
          setLoadingProgress(30);
          const regionData = await Promise.race([
            loadDataForRegion(regionInfo.region, currentConfig),
            new Promise<any[]>((_, reject) =>
              setTimeout(() => reject(new Error('Region data loading timeout after 30 seconds')), 30000)
            )
          ]);

          let californiaDataForAverage: any[] | undefined = undefined;
          if (currentConfig.id === 'political_lean') {
            setLoadingProgress(50);
            try {
              californiaDataForAverage = await Promise.race([
                loadRemainingCaliforniaData([], currentConfig),
                new Promise<any[]>((_, reject) =>
                  setTimeout(() => reject(new Error('California data loading timeout')), 20000)
                )
              ]);
            } catch (calError) {
              // Fallback to default average
            }
          }

          if (isStaleLoad()) return;
          if (regionData && regionData.length > 0) {
            allData = [...regionData];
            tractRowCache.set(tractCacheKey, allData);
            setTractData(allData);
            setLoadingProgress(90);
            const metrics = await calculateAndSetMetrics(allData, currentConfig, californiaDataForAverage);
            if (metrics && nextMapCenter && nextMapZoom) {
              mapCacheSet(loadKey, {
                tractData: allData,
                mapCenter: liveMapViewRef.current.center,
                mapZoom: liveMapViewRef.current.zoom,
                metrics
              });
            }
          } else {
            setError(`No data found for the selected region. Please check the browser console for details.`);
          }
        } catch (loadError) {
          console.error('Error loading region data:', loadError);
          setError(`Failed to load regional data: ${loadError instanceof Error ? loadError.message : 'Unknown error'}`);
        }
      } else if (targetCounty) {
        try {
          const { countyPrefix } = await resolveCountyPrefixForLocation(location);
          setCountyGeoidPrefix(countyPrefix);
        } catch {
          setCountyGeoidPrefix(null);
        }
        // Load county data
        // Normalize county name (remove " County" if present, then add it back for display)
        const normalizedCounty = targetCounty.replace(/ County$/i, '');
        setLoadingStage(`Loading ${normalizedCounty} County data...`);
        setLoadingProgress(20);

        // Set map center and zoom for the county
        const countyCenter = getMapCenter(`${normalizedCounty} County`);
        nextMapCenter = countyCenter;
        nextMapZoom = 9; // Zoom in to county level
        if (shouldResetMapView) {
          setMapCenter(nextMapCenter);
          setMapZoom(nextMapZoom);
          liveMapViewRef.current = { center: nextMapCenter, zoom: nextMapZoom };
          lastGeographyKeyRef.current = geographyKey;
        }

        try {
          setLoadingProgress(30);
          const countyData = await Promise.race([
            loadDataForCounty(`${normalizedCounty} County`, currentConfig),
            new Promise<any[]>((_, reject) => 
              setTimeout(() => reject(new Error('Data loading timeout after 30 seconds')), 30000)
            )
          ]);
          
          // For political lean, also load all California data to calculate accurate California average
          let californiaDataForAverage: any[] | undefined = undefined;
          if (currentConfig.id === 'political_lean') {
            setLoadingProgress(50);
            try {
              californiaDataForAverage = await Promise.race([
                loadRemainingCaliforniaData([], currentConfig),
                new Promise<any[]>((_, reject) => 
                  setTimeout(() => reject(new Error('California data loading timeout')), 20000)
                )
              ]);
            } catch (calError) {
              // Fallback to default average
            }
          }
          
          if (isStaleLoad()) return;
          if (countyData && countyData.length > 0) {
            allData = [...countyData];
            tractRowCache.set(tractCacheKey, allData);
            setTractData(allData);
            setLoadingProgress(90);
            const metrics = await calculateAndSetMetrics(allData, currentConfig, californiaDataForAverage);
            if (metrics && nextMapCenter && nextMapZoom) {
              mapCacheSet(loadKey, {
                tractData: allData,
                mapCenter: liveMapViewRef.current.center,
                mapZoom: liveMapViewRef.current.zoom,
                metrics
              });
            }
          } else {
            setError(`No data found for ${normalizedCounty} County. Please check the browser console for details.`);
          }
        } catch (loadError) {
          console.error('Error loading county data:', loadError);
          setError(`Failed to load ${normalizedCounty} County data: ${loadError instanceof Error ? loadError.message : 'Unknown error'}`);
        }
      } else {
        setError(`Could not determine county for location: ${location}`);
        setLoading(false);
        return;
      }

      setLoadingStage('Complete!');
      setLoadingProgress(100);

    } catch (err) {
      console.error('Error in data loading:', err);
      setError(`Failed to load data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      if (!isStaleLoad()) {
        setLoading(false);
        isLoadingRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentConfig?.id, location, dataScope]);

  // Load data for a specific county
  const loadDataForCounty = async (county: string, config: MapVariableConfig): Promise<any[]> => {
    try {
      if (config.dataSource === 'ev_by_tract_2023') {
        // Remove " County" suffix if present for matching
        const cleanCountyName = county.replace(/ County$/, '').toUpperCase();
        
        // Add timeout to the query
        const queryPromise = supabaseClient
          .from('ev_by_tract_2023')
          .select('*')
          .eq('County', cleanCountyName) // Use 'County' column (capitalized)
          .limit(5000);
        
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Query timeout after 20 seconds')), 20000)
        );
        
        const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any;
        
        if (error) {
          console.error('Error querying county data:', error);
          throw error;
        }
        return data || [];
      } else if (config.dataSource === 'cbei_tracts') {
        // For cbei_tracts, we need to match by county name in the 'name' field
        const { data, error } = await supabaseClient
          .from('cbei_tracts')
          .select('*')
          .eq('year', 2023) // Use year 2023 as specified
          .ilike('name', `%${county}%`)
          .limit(5000);
        
        if (error) throw error;
        return data || [];
      } else {
        // Get latest year
        const { data: yearRow, error: yearErr } = await supabaseClient
          .from(config.dataSource)
          .select('year')
          .order('year', { ascending: false })
          .limit(1);
        if (yearErr) throw yearErr;
        const latestYear = yearRow?.[0]?.year;

        const { data, error } = await supabaseClient
          .from(config.dataSource)
          .select('*')
          .eq('year', latestYear)
          .ilike('county', `%${county}%`)
          .limit(5000);
        
        if (error) throw error;
        return data || [];
      }
    } catch (err) {
      console.error('Error loading county data:', err);
      return [];
    }
  };

  // Load data for a specific region
  const loadDataForRegion = async (region: number, config: MapVariableConfig): Promise<any[]> => {
    try {
      // Get all counties in this region
      const { data: regionCounties, error: regionError } = await supabaseClient
        .from('regions')
        .select('county')
        .eq('region', region)
        .eq('geography_type', 'County'); // Use 'County' with capital C
      
      if (regionError) {
        console.error('Error fetching region counties:', regionError);
        throw regionError;
      }
      
      if (!regionCounties || regionCounties.length === 0) {
        return [];
      }

      const countyNames = regionCounties.map(r => r.county).filter(Boolean);
      
      if (countyNames.length === 0) {
        return [];
      }

      if (config.dataSource === 'ev_by_tract_2023') {
        // Clean county names for matching (remove " County" suffix and convert to uppercase)
        const cleanCountyNames = countyNames.map(name => name.replace(/ County$/, '').toUpperCase());
        
        const { data, error } = await supabaseClient
          .from('ev_by_tract_2023')
          .select('*')
          .in('County', cleanCountyNames) // Use 'County' column (capitalized)
          .limit(10000); // Increased limit for region data
        
        if (error) {
          console.error('Error querying region data:', error);
          throw error;
        }
        
        return data || [];
      } else if (config.dataSource === 'cbei_tracts') {
        // For cbei_tracts, match by county names in the 'name' field
        const { data, error } = await supabaseClient
          .from('cbei_tracts')
          .select('*')
          .eq('year', 2023) // Use year 2023 as specified
          .limit(10000);
        
        if (error) {
          console.error('Error querying cbei_tracts region data:', error);
          throw error;
        }
        
        // Filter by county names (since cbei_tracts doesn't have a direct county field)
        const filteredData = data?.filter(row => {
          const name = row.name?.toLowerCase() || '';
          return countyNames.some(county => name.includes(county.toLowerCase()));
        }) || [];
        
        return filteredData;
      } else {
        // Get latest year
        const { data: yearRow, error: yearErr } = await supabaseClient
          .from(config.dataSource)
          .select('year')
          .order('year', { ascending: false })
          .limit(1);
        if (yearErr) throw yearErr;
        const latestYear = yearRow?.[0]?.year;

        const { data, error } = await supabaseClient
          .from(config.dataSource)
          .select('*')
          .eq('year', latestYear)
          .in('county', countyNames)
          .limit(10000);
        
        if (error) throw error;
        return data || [];
      }
    } catch (err) {
      console.error('Error loading region data:', err);
      return [];
    }
  };

  // Load remaining California data
  const loadRemainingCaliforniaData = async (existingData: any[], config: MapVariableConfig): Promise<any[]> => {
    try {
      const existingTracts = new Set(existingData.map(d => d.tract));
      let allData: any[] = [];
      let offset = 0;
      const batchSize = 10000; // Large batch size to minimize requests
      
      if (config.dataSource === 'ev_by_tract_2023') {
        while (true) {
          let query = supabaseClient.from('ev_by_tract_2023').select('*');
          
          // Only exclude existing tracts if there are any
          if (existingTracts.size > 0) {
            query = query.not('tract', 'in', `(${Array.from(existingTracts).join(',')})`);
          }
          
          const { data, error } = await query.range(offset, offset + batchSize - 1);
          
          if (error) throw error;
          
          if (!data || data.length === 0) break;
          
          allData = allData.concat(data);
          offset += batchSize;
          
          // Safety check to prevent infinite loops
          if (data.length < batchSize) break;
        }
        
        return allData;
      } else if (config.dataSource === 'cbei_tracts') {
        while (true) {
          const { data, error } = await supabaseClient
            .from('cbei_tracts')
            .select('*')
            .eq('year', 2023) // Use year 2023 as specified
            .range(offset, offset + batchSize - 1);
          
          if (error) throw error;
          
          if (!data || data.length === 0) break;
          
          allData = allData.concat(data);
          offset += batchSize;
          
          // Safety check to prevent infinite loops
          if (data.length < batchSize) break;
        }
        
        return allData;
      } else {
        // Get latest year
        const { data: yearRow, error: yearErr } = await supabaseClient
          .from(config.dataSource)
          .select('year')
          .order('year', { ascending: false })
          .limit(1);
        if (yearErr) throw yearErr;
        const latestYear = yearRow?.[0]?.year;

        while (true) {
          const { data, error } = await supabaseClient
            .from(config.dataSource)
            .select('*')
            .eq('year', latestYear)
            .not('geofips', 'in', `(${Array.from(existingTracts).join(',')})`)
            .range(offset, offset + batchSize - 1);
          
          if (error) throw error;
          
          if (!data || data.length === 0) break;
          
          allData = allData.concat(data);
          offset += batchSize;
          
          // Safety check to prevent infinite loops
          if (data.length < batchSize) break;
        }
        
        return allData;
      }
    } catch (err) {
      console.error('Error loading remaining California data:', err);
      return [];
    }
  };

  const populationWeightedPoliticalMean = useCallback((rows: any[], field: string): number | null => {
    const weightedSum = rows.reduce((sum: number, t: any) => {
      const leanValue = t?.[field];
      const population = t?.total_population || 0;
      if (leanValue != null && !Number.isNaN(leanValue) && population > 0) {
        return sum + leanValue * population;
      }
      return sum;
    }, 0);
    const totalPopulation = rows.reduce((sum: number, t: any) => sum + (t?.total_population || 0), 0);
    if (totalPopulation <= 0) return null;
    return (weightedSum / totalPopulation) * 100;
  }, []);

  const locationType = useMemo(() => {
    const info = supabaseLocationService.getAvailableLocations().find((loc) => loc.name === location);
    if (!info) return 'unknown' as const;
    if (info.type === 'city') return 'city' as const;
    if (info.type === 'county') return 'county' as const;
    if (info.type === 'state') return 'state' as const;
    return 'unknown' as const;
  }, [location]);

  useEffect(() => {
    let cancelled = false;
    const locations = supabaseLocationService.getAvailableLocations();
    const info = locations.find((loc) => loc.name === location);
    const labels: MapScopeLabelContext = {};
    if (info?.type === 'city' && info.county) {
      labels.countyName = /county/i.test(info.county) ? info.county : `${info.county} County`;
    } else if (info?.type === 'county') {
      labels.countyName = info.name;
    }
    (async () => {
      if (dataScope === 'region') {
        try {
          const regionInfo = await getLocationRegionInfo(location);
          if (!cancelled && regionInfo) {
            labels.regionName = regionInfo.regionName;
            if (!labels.countyName) labels.countyName = `${regionInfo.county} County`;
          }
        } catch {
          // keep county-only labels
        }
      }
      if (!cancelled) setMapScopeLabels(labels);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, dataScope]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (locationType !== 'city' || !locationGeofips || tractData.length === 0) {
        if (!cancelled) setPlaceTractGeoids(null);
        return;
      }
      const countyGeoids = tractData
        .map((t) => normalizeCensusTractGeoid(String(t.tract ?? t.geofips ?? '')))
        .filter((g): g is string => Boolean(g));
      const tractSet = await fetchPlaceTractSet(String(locationGeofips), location, countyGeoids);
      if (!cancelled) setPlaceTractGeoids(tractSet.size > 0 ? tractSet : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [location, locationGeofips, locationType, tractData]);

  const buildMapMetrics = useCallback(
    (
      data: any[],
      config: MapVariableConfig,
      _statewideValues: number[] | null | undefined,
      _californiaRows?: any[]
    ): MapMetrics | null => {
      try {
        const variableId = config.id as MapVariableId;
        const refDef = MAP_REFERENCE_DEFINITIONS[variableId];
        const year = (data?.[0]?.year as number) || refDef?.year || 2023;

        const peerRows = data;
        const locationRows = rowsForSelectedLocation(peerRows, locationType, placeTractGeoids);
        const locLabel = locationKpiLabel(location, variableId);

        const jurisdiction = summarizeJurisdiction(
          locationRows,
          variableId,
          getDataFieldForVariable,
          populationWeightedPoliticalMean,
          { jurisdictionLabel: locLabel }
        );
        const tractValues = tractValuesFromRows(
          locationRows,
          variableId,
          getDataFieldForVariable
        );
        const medianTractValue =
          tractValues.length > 0
            ? variableId === 'income'
              ? jurisdiction.currentValue
              : (() => {
                  const sorted = [...tractValues].sort((a, b) => a - b);
                  const mid = Math.floor(sorted.length / 2);
                  return sorted.length % 2 === 0
                    ? (sorted[mid - 1] + sorted[mid]) / 2
                    : sorted[mid];
                })()
            : undefined;
        let zeroPortTractCount: number | undefined;
        if (variableId === 'total_chargers') {
          zeroPortTractCount = tractValues.filter((v) => v <= 0).length;
        }
        let evCountEstimated: boolean | undefined;
        if (variableId === 'total_evs' && locationRows[0]) {
          const row = locationRows[0] as Record<string, unknown>;
          const native =
            row.total_plugin_evs ?? row.total_evs ?? row.plugin_ev_count ?? row.ev_count;
          evCountEstimated = native == null || !Number.isFinite(Number(native));
        }

        const peerRef = summarizeMapScopePeer(
          peerRows,
          variableId,
          dataScope,
          mapScopeLabels,
          getDataFieldForVariable,
          populationWeightedPoliticalMean
        );

        const peerValue =
          peerRef.value != null && Number.isFinite(peerRef.value) ? peerRef.value : null;
        return {
          currentValue: jurisdiction.currentValue,
          californiaAverage: peerValue,
          year,
          variable: variableId,
          jurisdictionLabel: locLabel,
          referenceLabel: peerRef.label,
          ...(jurisdiction.minValue !== undefined && { minValue: jurisdiction.minValue }),
          ...(jurisdiction.maxValue !== undefined && { maxValue: jurisdiction.maxValue }),
          tractCount: locationRows.length,
          ...(medianTractValue !== undefined && { medianTractValue }),
          ...(zeroPortTractCount !== undefined && { zeroPortTractCount }),
          ...(evCountEstimated !== undefined && { evCountEstimated }),
        };
      } catch (metricsError) {
        console.error('Error calculating metrics:', metricsError);
        return null;
      }
    },
    [
      dataScope,
      getDataFieldForVariable,
      location,
      locationType,
      mapScopeLabels,
      placeTractGeoids,
      populationWeightedPoliticalMean,
    ]
  );

  const calculateAndSetMetrics = useCallback(
    async (data: any[], config: MapVariableConfig, californiaData?: any[]): Promise<MapMetrics | null> => {
      const metrics = buildMapMetrics(data, config, statewideBreakValues, californiaData);
      if (metrics) notifyDataLoaded(metrics);
      return metrics;
    },
    [buildMapMetrics, notifyDataLoaded, statewideBreakValues]
  );

  useEffect(() => {
    if (!currentConfig || tractData.length === 0) return;
    const metrics = buildMapMetrics(tractData, currentConfig, statewideBreakValues);
    if (metrics) notifyDataLoaded(metrics);
  }, [tractData, currentConfig, statewideBreakValues, buildMapMetrics, notifyDataLoaded]);

  // Load data based on selected variable and scope
  useEffect(() => {
    if (!currentConfig) {
      setError(`No configuration found for variable: ${selectedVariable}`);
      setLoading(false);
      return;
    }
    loadGenerationRef.current += 1;
    isLoadingRef.current = false;
    loadDataByLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariable, currentConfig?.id, location, dataScope]);

  /** Track pan/zoom so variable switches keep the user's viewport. */
  const MapViewportTracker: React.FC = () => {
    const map = useMap();
    useEffect(() => {
      const sync = () => {
        const c = map.getCenter();
        liveMapViewRef.current = {
          center: [c.lat, c.lng],
          zoom: map.getZoom(),
        };
      };
      sync();
      map.on('moveend', sync);
      map.on('zoomend', sync);
      return () => {
        map.off('moveend', sync);
        map.off('zoomend', sync);
      };
    }, [map]);
    return null;
  };

  /** Re-apply viewport after variable/data layer swap (MapContainer does not remount). */
  const MapViewportPreserve: React.FC<{ variableId: string }> = ({ variableId }) => {
    const map = useMap();
    const skipFirst = useRef(true);
    useLayoutEffect(() => {
      if (skipFirst.current) {
        skipFirst.current = false;
        return;
      }
      if (!isMountedRef.current) return;

      const { center, zoom } = liveMapViewRef.current;
      let cancelled = false;

      const apply = () => {
        if (cancelled || !isMountedRef.current || !isMapReadyForLayout(map)) return;
        safeInvalidateSize(map, () => {
          if (cancelled || !isMountedRef.current) return;
          try {
            map.setView(center, zoom, { animate: false });
          } catch {
            // map may be unmounting
          }
        });
      };

      try {
        map.whenReady(() => {
          requestAnimationFrame(() => requestAnimationFrame(apply));
        });
      } catch {
        // ignore teardown
      }

      return () => {
        cancelled = true;
      };
    }, [variableId, map]);
    return null;
  };

  /** Re-measure when the chart container resizes (e.g. section scroll-in or layout change). */
  const MapResizeSync: React.FC = () => {
    const map = useMap();
    useEffect(() => {
      const el = map.getContainer?.();
      if (!el) return;
      let ro: ResizeObserver | null = null;
      const onResize = () => {
        if (!isMountedRef.current) return;
        safeInvalidateSize(map);
      };
      try {
        ro = new ResizeObserver(() => onResize());
        ro.observe(el);
      } catch {
        map.on('resize', onResize);
      }
      return () => {
        ro?.disconnect();
        map.off('resize', onResize);
      };
    }, [map]);
    return null;
  };

  // Component to safely enable scroll wheel zoom after map is ready
  const EnableScrollWheelZoom: React.FC<{ enabled: boolean }> = ({ enabled }) => {
    const map = useMap();
    useEffect(() => {
      if (!enabled) {
        // Disable scroll wheel zoom if not ready
        try {
          if (map && map.scrollWheelZoom && map.scrollWheelZoom.enabled()) {
            map.scrollWheelZoom.disable();
          }
        } catch (err) {
          // Ignore errors when disabling
        }
        return;
      }
      
      // Add a longer delay and check if map container exists
      const timer = setTimeout(() => {
        try {
          if (!map || !map.getContainer) return;
          
          const container = map.getContainer();
          if (!container) return;
          
          // Check if container has the required Leaflet properties
          // Use type assertion to access Leaflet's internal property
          const leafletContainer = container as any;
          if (leafletContainer._leaflet_id === undefined) {
            // Retry after a bit more time
            setTimeout(() => {
              try {
                if (map.scrollWheelZoom && !map.scrollWheelZoom.enabled()) {
                  map.scrollWheelZoom.enable();
                }
              } catch (err) {
                // Scroll wheel zoom not available yet
              }
            }, 300);
            return;
          }
          
          // Only enable if map is fully initialized
          if (map.scrollWheelZoom && !map.scrollWheelZoom.enabled()) {
            map.scrollWheelZoom.enable();
          }
        } catch (err) {
          // Silently catch errors - map might not be ready yet
        }
      }, 800); // Longer delay to ensure map is fully ready
      
      return () => clearTimeout(timer);
    }, [enabled, map]);
    return null;
  };

  useEffect(() => {
    if (!currentConfig) return;
    const varId = currentConfig.id as MapVariableId;
    if (!MAP_VARIABLE_SEMANTICS[varId]) return;

    if (dataScope === 'california') {
      setStatewideBreakValues(null);
      setUsesStatewideBreaks(true);
      return;
    }

    let cancelled = false;
    (async () => {
      const values = await fetchStatewideNumericValues(currentConfig, getDataFieldForVariable);
      if (!cancelled) {
        setStatewideBreakValues(values);
        setUsesStatewideBreaks(Boolean(values && values.length > 0));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentConfig, dataScope, getDataFieldForVariable]);

  const filteredGeoJson = useMemo(() => {
    if (!geoJsonData) return null;
    if (!countyGeoidPrefix || dataScope === 'california') {
      return geoJsonData;
    }
    const prefix = countyGeoidPrefix;
    const features = geoJsonData.features.filter((f: { properties?: { GEOID?: string } }) => {
      const geoid = f.properties?.GEOID;
      return typeof geoid === 'string' && geoid.startsWith(prefix);
    });
    return { ...geoJsonData, features };
  }, [geoJsonData, countyGeoidPrefix, dataScope]);

  const tractLookup = useMemo(() => {
    const byGeoid = new Map<string, TractData[]>();
    for (const row of tractData) {
      const key = normalizeCensusTractGeoid(row.tract ?? row.geofips);
      if (!key) continue;
      const list = byGeoid.get(key) ?? [];
      list.push(row);
      byGeoid.set(key, list);
    }
    const resolved = new Map<string, TractData>();
    for (const [key, rows] of byGeoid) {
      const expectedCounty = getCountyFromGeoid(key);
      const match =
        expectedCounty != null ? rows.find((r) => r.County === expectedCounty) : undefined;
      resolved.set(key, match ?? rows[0]);
    }
    return resolved;
  }, [tractData]);

  const resolveTractForGeoid = useCallback(
    (geoid: string, config: MapVariableConfig | null | undefined): TractData | undefined => {
      if (!config) return undefined;
      if (config.dataSource === 'cbei_tracts' || config.dataSource === 'acs5_income') {
        const geoidNorm = normalizeCensusTractGeoid(geoid);
        if (!geoidNorm) return undefined;
        return tractData.find((t) => normalizeCensusTractGeoid(t.geofips) === geoidNorm);
      }
      const geoidNorm = normalizeCensusTractGeoid(geoid);
      if (!geoidNorm) return undefined;
      return tractLookup.get(geoidNorm);
    },
    [tractData, tractLookup]
  );

  const classification = useMemo(() => {
    if (!currentConfig || tractData.length === 0) return null;
    const varId = currentConfig.id as MapVariableId;
    if (!MAP_VARIABLE_SEMANTICS[varId]) return null;

    let values: number[];
    let breakContext: string;
    if (dataScope === 'california' || (usesStatewideBreaks && statewideBreakValues && statewideBreakValues.length > 0)) {
      values =
        statewideBreakValues && statewideBreakValues.length > 0
          ? statewideBreakValues
          : extractNumericValuesForVariable(tractData, varId, getDataFieldForVariable);
      breakContext = 'Breaks based on California census tracts, 2023';
    } else {
      values = extractNumericValuesForVariable(tractData, varId, getDataFieldForVariable);
      breakContext = 'Breaks based on loaded geography';
    }
    return buildClassificationForVariable(values, varId, breakContext);
  }, [
    currentConfig,
    tractData,
    dataScope,
    statewideBreakValues,
    usesStatewideBreaks,
    getDataFieldForVariable,
  ]);

  const legendReferenceValue = useMemo(() => {
    if (!currentConfig || tractData.length === 0) return undefined;
    const varId = currentConfig.id as MapVariableId;
    if (!MAP_VARIABLE_SEMANTICS[varId]) return currentConfig.legendConfig?.referenceValue;
    const peer = summarizeMapScopePeer(
      tractData,
      varId,
      dataScope,
      mapScopeLabels,
      getDataFieldForVariable,
      populationWeightedPoliticalMean
    );
    return { label: peer.label, value: peer.value };
  }, [
    currentConfig,
    tractData,
    dataScope,
    mapScopeLabels,
    getDataFieldForVariable,
    populationWeightedPoliticalMean,
  ]);

  const buildTractExportRows = useCallback((): Record<string, unknown>[] => {
    if (!currentConfig || tractData.length === 0) return [];
    const variableId = currentConfig.id;
    const dataField = getDataFieldForVariable(variableId);

    return tractData.map((tract) => {
      let value: number | null = null;
      if (variableId === 'carbon_footprint') {
        const totalEmissions =
          (tract.transportation_total_emissions || 0) +
          (tract.housing_total_emissions || 0) +
          (tract.food_total_emissions || 0) +
          (tract.goods_total_emissions || 0) +
          (tract.services_total_emissions || 0);
        value = totalEmissions / 1_000_000;
      } else if (variableId === 'zero_vehicle_households') {
        value = zeroVehicleHouseholdPctFromRow(tract);
      } else if (variableId === 'total_evs') {
        value = tractTotalEvsFromRow(tract);
      } else {
        const raw = tract[dataField];
        value = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
        if (value != null && (variableId === 'political_lean' || variableId === 'owner_occupied_housing' || variableId === 'educational_attainment')) {
          value *= 100;
        }
      }

      return {
        location,
        variable: variableId,
        geofips: tract.geofips,
        county: tract.county ?? tract.County ?? null,
        tract_name: tract.location_name ?? null,
        value,
      };
    });
  }, [currentConfig, tractData, getDataFieldForVariable, location]);

  const handleDownloadPNG = useCallback(() => {
    const label = currentConfig?.name?.replace(/[^a-zA-Z0-9]+/g, '_') ?? 'Map';
    void downloadDomAsPNG(mapContainerRef.current, formatFilename(location, label));
  }, [location, currentConfig?.name]);

  const handleDownloadCSV = useCallback(() => {
    const rows = buildTractExportRows();
    if (!rows.length) return;
    const label = currentConfig?.id?.replace(/[^a-zA-Z0-9]+/g, '_') ?? 'Map';
    downloadDataAsCSV(rows, formatFilename(location, `${label}_Tract_Data`));
  }, [buildTractExportRows, location, currentConfig?.id]);

  useEffect(() => {
    onDownloadPNGReady?.(handleDownloadPNG);
  }, [onDownloadPNGReady, handleDownloadPNG]);

  useEffect(() => {
    onDownloadCSVReady?.(handleDownloadCSV);
  }, [onDownloadCSVReady, handleDownloadCSV]);

  // Get style for each feature
  const getStyle = (feature: any) => {
    if (!currentConfig || !tractData.length) {
      return choroplethNoDataPathStyle();
    }

    const geoid = feature.properties.GEOID;
    const tract = resolveTractForGeoid(geoid, currentConfig);
    
    if (!tract) {
      return choroplethNoDataPathStyle();
    }

    const dataField = getDataFieldForVariable(currentConfig.id);
    let value;
    
    // Special handling for carbon footprint - calculate from total emissions
    if (currentConfig.id === 'carbon_footprint') {
      const totalEmissions = tract.transportation_total_emissions + 
                            tract.housing_total_emissions + 
                            tract.food_total_emissions + 
                            tract.goods_total_emissions + 
                            tract.services_total_emissions;
      value = totalEmissions; // Data is already per-household, just use directly
    } else if (currentConfig.id === 'zero_vehicle_households') {
      value = zeroVehicleHouseholdPctFromRow(tract);
    } else if (currentConfig.id === 'total_evs') {
      value = tractTotalEvsFromRow(tract);
    } else {
      value = tract[dataField];
    }
    
    
    if (value === null || value === undefined || Number.isNaN(value)) {
      return choroplethNoDataPathStyle();
    }

    let numericValue = typeof value === 'string' ? parseFloat(String(value).replace(/[,]/g, '')) : value;
    
    if (!Number.isFinite(numericValue)) {
      return choroplethNoDataPathStyle();
    }

    // Convert decimal to percentage for political lean
    if (currentConfig.id === 'political_lean') {
      numericValue = numericValue * 100;
    }

    // Convert decimal to percentage for owner-occupied housing (0.553 = 55.3%)
    if (currentConfig.id === 'owner_occupied_housing' || currentConfig.id === 'educational_attainment') {
      numericValue = numericValue * 100;
    }

    // Convert grams to metric tons for carbon footprint (divide by 1,000,000)
    if (currentConfig.id === 'carbon_footprint') {
      numericValue = numericValue / 1000000;
    }

    if (!classification?.bins?.length) {
      return choroplethNoDataPathStyle();
    }

    const color = currentConfig.colorFunction(numericValue, classification.bins);

    return choroplethDataPathStyle(color);
  };

  // Handle feature click
  const onEachFeature = (feature: any, layer: any) => {
    const geoid = feature.properties.GEOID;
    const tract: any = resolveTractForGeoid(geoid, currentConfig);
    
    if (tract && currentConfig) {
      const dataField = getDataFieldForVariable(currentConfig.id);
      let value = tract[dataField];
      
      // Special handling for zero_vehicle_households: calculate count from percentage
      // Note: pct_zero_vehicle_households is stored as a decimal (0.085 = 8.5%), not as a percentage
      if (currentConfig.id === 'zero_vehicle_households') {
        value = zeroVehicleHouseholdPctFromRow(tract);
      } else if (currentConfig.id === 'total_evs') {
        value = tractTotalEvsFromRow(tract);
      }
      
      // Convert decimal to percentage for owner-occupied housing and educational attainment display
      if ((currentConfig.id === 'owner_occupied_housing' || currentConfig.id === 'educational_attainment') && value !== null && value !== undefined) {
        value = typeof value === 'number' ? value * 100 : parseFloat(value) * 100;
      }
      
      // Helper function to format political lean as "D +15" or "R +2"
      const formatPoliticalLean = (leanValue: number): string => {
        if (leanValue > 0) {
          return `D +${leanValue.toFixed(1)}`;
        } else if (leanValue < 0) {
          return `R +${Math.abs(leanValue).toFixed(1)}`;
        } else {
          return 'Even';
        }
      };
      
      const displayValue = value !== null && value !== undefined ? 
        (typeof value === 'number' ? value.toLocaleString() : value) : 'No data';
      
      // Special popup for carbon footprint with category breakdown
      if (currentConfig.id === 'carbon_footprint') {
        // Helper function to convert grams to metric tons (data is already per-household)
        const convertToMetricTons = (value: any) => {
          if (value === null || value === undefined) return 'No data';
          const numericValue = typeof value === 'string' ? parseFloat(value) : value;
          if (isNaN(numericValue)) return 'No data';
          return (numericValue / 1000000).toFixed(1);
        };

        layer.bindPopup(`
          <div>
            <strong>Census Tract ${geoid}</strong><br/>
            <strong>Total Carbon Footprint:</strong> ${convertToMetricTons(tract.transportation_total_emissions + tract.housing_total_emissions + tract.food_total_emissions + tract.goods_total_emissions + tract.services_total_emissions)} MT CO₂e/HH<br/>
            <strong>Transportation:</strong> ${convertToMetricTons(tract.transportation_total_emissions)} MT CO₂e/HH<br/>
            <strong>Housing:</strong> ${convertToMetricTons(tract.housing_total_emissions)} MT CO₂e/HH<br/>
            <strong>Food:</strong> ${convertToMetricTons(tract.food_total_emissions)} MT CO₂e/HH<br/>
            <strong>Goods:</strong> ${convertToMetricTons(tract.goods_total_emissions)} MT CO₂e/HH<br/>
            <strong>Services:</strong> ${convertToMetricTons(tract.services_total_emissions)} MT CO₂e/HH<br/>
            <strong>Location:</strong> ${tract.name || 'Unknown'}
          </div>
        `);
      } else if (currentConfig.id === 'political_lean') {
        // Special formatting for political lean: "D +15" or "R +2"
        // Convert from decimal to percentage (e.g., 0.15 -> 15) to match style function
        let numericValue = typeof value === 'number' ? value : parseFloat(value);
        if (!isNaN(numericValue)) {
          numericValue = numericValue * 100; // Convert decimal to percentage
        }
        const formattedValue = !isNaN(numericValue) ? formatPoliticalLean(numericValue) : 'No data';
        
        layer.bindPopup(`
          <div>
            <strong>Census Tract ${geoid}</strong><br/>
            <strong>${currentConfig.legendConfig?.title}:</strong> ${formattedValue}<br/>
            <strong>County:</strong> ${tract.County || 'Unknown'}
          </div>
        `);
      } else {
        // Format value with unit for display
        let formattedValue = displayValue;
        if (value !== null && value !== undefined && displayValue !== 'No data') {
          const unit = currentConfig.legendConfig?.unit || '';
          if (unit === ' min') {
            formattedValue = `${parseFloat(value.toString()).toFixed(1)}${unit}`;
          } else if (unit === '%') {
            formattedValue = `${parseFloat(value.toString()).toFixed(1)}${unit}`;
          } else if (unit === ' MT CO₂e/HH') {
            formattedValue = `${parseFloat(value.toString()).toFixed(1)}${unit}`;
          } else {
            formattedValue = typeof value === 'number' ? value.toLocaleString() + (unit ? ` ${unit}` : '') : value;
          }
        }
        
        layer.bindPopup(`
          <div>
            <strong>Census Tract ${geoid}</strong><br/>
            <strong>${currentConfig.legendConfig?.title}:</strong> ${formattedValue}<br/>
            <strong>County:</strong> ${tract.County || 'Unknown'}
          </div>
        `);
      }
    }

    const baseStyle = getStyle(feature);
    layer.on({
      mouseover: (e: { target: { setStyle: (s: object) => void; bringToFront: () => void } }) => {
        e.target.setStyle(choroplethHoverPathStyle(baseStyle));
        e.target.bringToFront();
      },
      mouseout: (e: { target: { setStyle: (s: object) => void } }) => {
        e.target.setStyle(baseStyle);
      },
    });
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px',
        padding: '20px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            width: '200px', 
            height: '4px', 
            backgroundColor: '#e0e0e0', 
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${loadingProgress}%`,
              height: '100%',
              backgroundColor: '#007bff',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
          {loadingStage}
        </div>
        <div style={{ fontSize: '14px', color: '#666' }}>
          {loadingProgress}% complete
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px',
        color: '#dc3545'
      }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
            Error loading map data
          </div>
          <div style={{ fontSize: '14px' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!filteredGeoJson) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px'
      }}>
        <div>Loading map data...</div>
      </div>
    );
  }

  if (loading && !classification && tractData.length > 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '400px'
      }}>
        <div>Calculating map statistics...</div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <div style={{ 
        marginBottom: '10px', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '20px',
        flexWrap: 'wrap'
      }}>
        {!hideVariableSelector && !lockedVariable && (
          <MapVariableSelector
            selectedVariable={selectedVariable}
            onVariableChange={setSelectedVariable}
            allowedVariables={allowedVariables}
          />
        )}
        {enableScopeSelector && (
          <DataLoadingScopeSelector
            selectedScope={dataScope}
            onScopeChange={setDataScope}
            disabled={loading}
          />
        )}
      </div>
      
      <div ref={mapContainerRef} style={{ position: 'relative', height: `${mapHeightPx}px` }}>
        <MapContainer
          key={`map-${location}-${dataScope}`}
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
          zoomControl={true}
          doubleClickZoom={true}
          dragging={true}
          whenReady={() => {
            setTimeout(() => {
              if (!isMountedRef.current) return;
              try {
                const mapElement = document.querySelector('.leaflet-container');
                if (mapElement && isMountedRef.current) {
                  setMapReady(true);
                } else {
                  setTimeout(() => {
                    if (isMountedRef.current) setMapReady(true);
                  }, 300);
                }
              } catch (err) {
                if (isMountedRef.current) setTimeout(() => setMapReady(true), 500);
              }
            }, 500);
          }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            opacity={CHOROPLETH_BASEMAP_OPACITY}
          />
          <MapViewportTracker />
          <MapViewportPreserve variableId={selectedVariable} />
          <MapResizeSync />
          {mapReady && <EnableScrollWheelZoom enabled={mapReady} />}
          {filteredGeoJson && classification && (
            <GeoJSON
              key={`geojson-${selectedVariable}-${tractData.length}`}
              data={filteredGeoJson}
              style={getStyle}
              onEachFeature={onEachFeature}
            />
          )}
        </MapContainer>
        
        {currentConfig && tractData.length > 0 && classification && (
          <div className="map-legend-wrapper">
            <MapLegend
              title={currentConfig.legendConfig?.title || 'Data'}
              dataYear={currentConfig.legendConfig?.dataYear || '2023'}
              colorScale={[]}
              breaks={[]}
              unit={currentConfig.legendConfig?.unit || ''}
              tractCount={tractData.length}
              referenceValue={legendReferenceValue || currentConfig.legendConfig?.referenceValue || undefined}
              bins={classification.bins}
              breakContext={classification.breakContext}
              directionHint={
                currentConfig.id === 'political_lean' ? undefined : 'Darker = more'
              }
              mapVariableId={currentConfig.id as MapVariableId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericMapTiered;
